export const formatDateToDDMMYYYY = (dateString: string | Date) => {
  const date = new Date(dateString);

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

export const formatDateToMMDDYYYY = (dateString: string | Date) => {
  const date = new Date(dateString);

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
};

export const formatDateKor = (dateString: string) => {
  const parts = dateString.split('-');

  if (parts.length !== 3) {
    return NaN;
  }

  const day = Number(parts[2]);
  const month = Number(parts[1]) - 1;
  const year = Number(parts[0]);

  const date = new Date(year, month, day);

  if (Number.isNaN(date.getTime())) {
    return NaN;
  }

  const formattedMonth = String(date.getMonth() + 1).padStart(2, '0');
  const formattedDay = String(date.getDate()).padStart(2, '0');
  const formattedYear = date.getFullYear();

  return `${formattedMonth}/${formattedDay}/${formattedYear}`;
};

// Inverse of formatDateToDDMMYYYY, for a date-of-birth field whose display
// format is DD/MM/YYYY but whose stored/submitted value must stay YYYY-MM-DD
// (the backend's format, unchanged). '' for anything not a complete,
// well-formed date - callers that validate on submit (not per keystroke)
// treat that as "not yet a valid date" the same way they already treat a
// malformed typed value.
export const parseDDMMYYYYToISO = (value: string): string => {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim());
  if (!match) return '';
  const [, day, month, year] = match as unknown as [string, string, string, string];
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  const isRealDate =
    date.getFullYear() === Number(year) &&
    date.getMonth() === Number(month) - 1 &&
    date.getDate() === Number(day);
  if (!isRealDate) return '';
  return `${year}-${month}-${day}`;
};

export const formatDateToYYYYMMDD = (dateString: string | Date) => {
  const date = dateString instanceof Date ? dateString : new Date(dateString);

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();

  return `${year}-${month}-${day}`;
};

const SHORT_MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export const formatDateToDDMonYYYY = (dateString: string | Date) => {
  const date = dateString instanceof Date ? dateString : new Date(dateString);

  const day = date.getDate().toString().padStart(2, '0');
  const month = SHORT_MONTH_NAMES[date.getMonth()];
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
};

export const formatDateTime = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

export const getLocalISOString = (date: string | Date) => {
  if (!(date instanceof Date)) {
    date = new Date(date);
  }
  const timezoneOffset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - timezoneOffset * 60000);
  const isoString = localDate.toISOString();
  return isoString.split('.')[0] ?? '';
};

export const formatFullDateTime = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

// Same as formatFullDateTime, but DD/MM/YYYY (Audrey) - scoped to the PSC/
// Report Management screens that asked for it. formatFullDateTime itself is
// left as YYYY-MM-DD for its other callers across the app.
export const formatFullDateTimeDDMMYYYY = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

export const formatTimeOnly = (date: string | Date) => {
  const d = date instanceof Date ? date : new Date(date);

  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');

  return `${hours}:${minutes}:${seconds}`;
};
